import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Table, DatePicker, Spin, Tag, Statistic, Button, message } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InboxOutlined,
  CarOutlined,
  ClockCircleOutlined,
  HistoryOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import api from '../services/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const Report = () => {
  const [range, setRange] = useState([dayjs(), dayjs()]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(async (start, end) => {
    setLoading(true);
    try {
      const res = await api.get('/reports/daily', {
        params: { startDate: start.format('YYYY-MM-DD'), endDate: end.format('YYYY-MM-DD') },
      });
      setData(res.data);
    } catch (error) {
      message.error('Тайлан татахад алдаа гарлаа');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (range && range[0] && range[1]) fetchReport(range[0], range[1]);
  }, [range, fetchReport]);

  const driverColumns = [
    { title: 'Хүргэлтийн ажилтан', dataIndex: 'driverName', key: 'driverName' },
    { title: 'Утас', dataIndex: 'driverPhone', key: 'driverPhone', width: 110 },
    { title: 'Нийт', dataIndex: 'total', key: 'total', width: 70 },
    { title: 'Өнөөдрийн', dataIndex: 'todayNew', key: 'todayNew', width: 90,
      render: v => <Tag color="blue">{v || 0}</Tag> },
    { title: 'Өмнөхөөс', dataIndex: 'fromPrevious', key: 'fromPrevious', width: 90,
      render: v => <Tag color="orange">{v || 0}</Tag> },
    {
      title: 'Хүргэсэн', dataIndex: 'delivered', key: 'delivered', width: 90,
      render: v => <Tag color="green">{v}</Tag>,
    },
    {
      title: 'Цуцалсан', dataIndex: 'failed', key: 'failed', width: 90,
      render: v => v > 0 ? <Tag color="red">{v}</Tag> : <span>{v}</span>,
    },
    {
      title: 'Хүргэлтэнд', key: 'onway', width: 100,
      render: (_, r) => {
        const onway = r.total - r.delivered - r.failed;
        return onway > 0 ? <Tag color="blue">{onway}</Tag> : <span>0</span>;
      },
    },
    {
      title: 'Шалтгаан', key: 'failReasons',
      render: (_, r) => {
        const reasons = Object.entries(r.failReasons || {});
        if (reasons.length === 0) return '-';
        return reasons.map(([reason, count]) => (
          <Tag key={reason} color="volcano" style={{ marginBottom: 2 }}>
            {reason}: {count}
          </Tag>
        ));
      },
    },
  ];

  const handleExportExcel = () => {
    const sd = range[0].format('YYYY-MM-DD');
    const ed = range[1].format('YYYY-MM-DD');
    const url = `${api.defaults.baseURL}/reports/export-excel?startDate=${sd}&endDate=${ed}`;
    const token = localStorage.getItem('merchant_token');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `tailan_${sd}_${ed}.xlsx`;
        a.click();
      });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0 }}>Тайлан</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Button icon={<DownloadOutlined />} onClick={handleExportExcel}>Excel</Button>
          <RangePicker
            value={range}
            onChange={r => r && r[0] && r[1] && setRange(r)}
            allowClear={false}
            format="YYYY-MM-DD"
            disabledDate={current => current && current < dayjs('2026-05-01').startOf('day')}
          />
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
      ) : data ? (
        <>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={12} sm={8} md={4}>
              <Card><Statistic title="Нийт" value={data.summary.total} prefix={<InboxOutlined />} /></Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card><Statistic title="Өнөөдрийн" value={data.summary.todayNew || 0}
                prefix={<ClockCircleOutlined />} valueStyle={{ color: '#5b21b6' }} /></Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card><Statistic title="Өмнөх өдрөөс" value={data.summary.fromPrevious || 0}
                prefix={<HistoryOutlined />} valueStyle={{ color: '#d97706' }} /></Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card><Statistic title="Хүргэсэн" value={data.summary.delivered}
                prefix={<CheckCircleOutlined />} valueStyle={{ color: '#3f8600' }} /></Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card><Statistic title="Цуцалсан" value={data.summary.failed}
                prefix={<CloseCircleOutlined />} valueStyle={{ color: '#cf1322' }} /></Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card><Statistic title="Хүргэлтэнд"
                value={data.summary.total - data.summary.delivered - data.summary.failed}
                prefix={<CarOutlined />} valueStyle={{ color: '#1890ff' }} /></Card>
            </Col>
          </Row>

          {Object.keys(data.failReasons).length > 0 && (
            <Card title="Цуцалсан шалтгаанууд" style={{ marginBottom: 24 }} size="small">
              <Row gutter={[16, 8]}>
                {Object.entries(data.failReasons)
                  .sort((a, b) => b[1] - a[1])
                  .map(([reason, count]) => (
                    <Col key={reason}>
                      <Tag color="volcano" style={{ fontSize: 14, padding: '4px 12px' }}>
                        {reason}: {count}
                      </Tag>
                    </Col>
                  ))}
              </Row>
            </Card>
          )}

          <Card title="Хүргэлтийн ажилтан бүрээр" size="small">
            <Table
              dataSource={data.byDriver}
              columns={driverColumns}
              rowKey="driverId"
              pagination={false}
              size="small"
              summary={pageData => {
                if (!pageData.length) return null;
                const total = pageData.reduce((s, r) => s + r.total, 0);
                const todayNew = pageData.reduce((s, r) => s + (r.todayNew || 0), 0);
                const fromPrev = pageData.reduce((s, r) => s + (r.fromPrevious || 0), 0);
                const delivered = pageData.reduce((s, r) => s + r.delivered, 0);
                const failed = pageData.reduce((s, r) => s + r.failed, 0);
                const onway = total - delivered - failed;
                return (
                  <Table.Summary.Row style={{ fontWeight: 'bold', background: '#fafafa' }}>
                    <Table.Summary.Cell index={0}>Нийт</Table.Summary.Cell>
                    <Table.Summary.Cell index={1}></Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>{total}</Table.Summary.Cell>
                    <Table.Summary.Cell index={3}><Tag color="blue">{todayNew}</Tag></Table.Summary.Cell>
                    <Table.Summary.Cell index={4}><Tag color="orange">{fromPrev}</Tag></Table.Summary.Cell>
                    <Table.Summary.Cell index={5}><Tag color="green">{delivered}</Tag></Table.Summary.Cell>
                    <Table.Summary.Cell index={6}><Tag color="red">{failed}</Tag></Table.Summary.Cell>
                    <Table.Summary.Cell index={7}><Tag color="blue">{onway}</Tag></Table.Summary.Cell>
                    <Table.Summary.Cell index={8}></Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
            />
          </Card>
        </>
      ) : null}
    </div>
  );
};

export default Report;
